import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2, Tag, X } from "lucide-react";
import { money } from "../useProducts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const input = "h-12 px-4 rounded-xl bg-[#ffffff] border border-[#e3e2dc] text-sm outline-none focus:border-[#ff2a85]/60 transition-colors";

export const DestinationToggle = ({ value, onChange }) => (
  <div data-testid="destination-toggle" className="grid grid-cols-2 gap-2 p-1 rounded-full bg-[#f3f2ec]">
    {[["us", "United States"], ["intl", "International"]].map(([id, label]) => (
      <button key={id} type="button" data-testid={`destination-${id}`} onClick={() => onChange(id)} className={`h-10 rounded-full font-mono-d text-[10px] uppercase tracking-[0.2em] transition-colors ${value === id ? "bg-[#0a0a0b] text-white" : "text-[#5f5e5a] hover:text-[#0a0a0b]"}`}>
        {label}
      </button>
    ))}
  </div>
);

export const useShippingRates = (destination) => {
  const [rates, setRates] = useState(null);
  useEffect(() => {
    setRates(null);
    axios.get(`${API}/shipping/rates`, { params: { destination } }).then((r) => setRates(r.data)).catch(() => setRates([]));
  }, [destination]);
  return rates;
};

export const ShippingOptions = ({ rates, subtotal, value, onChange }) => {
  if (rates === null) return <div className="h-16 flex items-center justify-center"><Loader2 size={16} className="animate-spin text-[#5f5e5a]" /></div>;
  if (!rates.length) return <p data-testid="shipping-none" className="text-sm text-[#5f5e5a]">No delivery options for this destination yet — contact us and we'll sort it.</p>;
  return (
    <div data-testid="shipping-options" className="space-y-2">
      {rates.map((r) => {
        const free = r.free_over_cents != null && subtotal >= r.free_over_cents;
        const active = value === r.id;
        return (
          <button key={r.id} type="button" data-testid={`shipping-option-${r.id}`} onClick={() => onChange(r.id)} className={`w-full flex items-center justify-between gap-4 rounded-2xl border px-4 py-3.5 text-left transition-all ${active ? "border-[#0a0a0b] bg-[#0a0a0b] text-white" : "border-[#e3e2dc] bg-[#ffffff] hover:border-[#ff2a85]"}`}>
            <span className="flex items-center gap-3">
              <span className={`h-4 w-4 rounded-full border flex items-center justify-center ${active ? "border-[#ff2a85] bg-[#ff2a85]" : "border-[#c9c8c2]"}`}>{active && <Check size={10} strokeWidth={3} />}</span>
              <span>
                <span className="block font-display font-extrabold uppercase text-[12px]">{r.label}</span>
                <span className={`block text-[11px] mt-0.5 ${active ? "text-white/60" : "text-[#5f5e5a]"}`}>{r.description || `${r.min_days}-${r.max_days} business days`}{!free && r.free_over_cents != null ? ` · Free over ${money(r.free_over_cents)}` : ""}</span>
              </span>
            </span>
            <span className={`font-mono-d text-sm ${active ? "text-[#ff2a85]" : ""}`}>{free || r.amount_cents === 0 ? "Free" : money(r.amount_cents)}</span>
          </button>
        );
      })}
    </div>
  );
};

export const CouponBox = ({ subtotal, coupon, onApply, onClear }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const apply = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/coupons/validate`, { code: code.trim(), subtotal_cents: subtotal });
      onApply(r.data);
      toast.success(`Coupon ${r.data.code} applied`);
      setCode("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not apply that code.");
    } finally {
      setBusy(false);
    }
  };
  if (coupon) {
    return (
      <div data-testid="coupon-applied" className="flex items-center justify-between gap-3 rounded-2xl border border-[#007a3d]/30 bg-[#007a3d]/5 px-4 py-3">
        <span className="flex items-center gap-2 text-sm"><Tag size={14} className="text-[#007a3d]" /><span className="font-mono-d tracking-[0.15em]">{coupon.code}</span><span className="text-[#5f5e5a] text-xs">{coupon.kind === "percent" ? `${coupon.value}% off` : `$${coupon.value} off`}</span></span>
        <button type="button" data-testid="coupon-remove" onClick={onClear} aria-label="Remove coupon" className="text-[#5f5e5a] hover:text-[#ff2a85]"><X size={14} /></button>
      </div>
    );
  }
  return (
    <form data-testid="coupon-form" onSubmit={apply} className="flex gap-2">
      <input data-testid="coupon-input" className={`${input} flex-1 font-mono-d uppercase tracking-[0.15em]`} placeholder="Have a coupon?" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />
      <button data-testid="coupon-apply" type="submit" disabled={busy || !code.trim()} className="h-12 px-5 rounded-xl bg-[#0a0a0b] hover:bg-[#ff2a85] text-white font-mono-d text-[10px] uppercase tracking-[0.2em] disabled:opacity-50 transition-colors">
        {busy ? <Loader2 size={14} className="animate-spin" /> : "Apply"}
      </button>
    </form>
  );
};
